import React from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { StackPanel, Typography, useNotificationEvents } from '@agilepoint/wat-react-ux';
import { ISaveTextDataResponse } from 'modules/shared/queries';
import { StyledFlexBox } from 'common';
import { CompanyDetailsForm } from './company-details-form';
import { TabFooterButtons } from '../tab-footer-buttons';

export interface ICompanyDetailsProps {
    mutationLoading: boolean;
    companyName: string;
    companyInfoEmail: string;
    companySupportEmail: string;
    companyWebsite: string;
    copyrightInfo: string;
    eulaLink: string;
    eulaText: string;
    onSaveCompanyDetails: (args: ICompanyDetailsFormFields) => Promise<ISaveTextDataResponse>;
}

export interface ICompanyDetailsFormFields {
    companyName: string;
    companyInfoEmail: string;
    companySupportEmail: string;
    companyWebsite: string;
    copyrightInfo: string;
    eulaLink: string;
    eulaText: string;
    eulaOptions: string;
}

const CompanyDetailsBase = (props: Pick<ICompanyDetailsProps, 'onSaveCompanyDetails' | 'mutationLoading'>) => {
    const { onSaveCompanyDetails, mutationLoading } = props;
    const { handleSubmit } = useFormContext<ICompanyDetailsFormFields>();
    const { showNotifications } = useNotificationEvents();
    const translate = useTranslate();

    const onSaveClick = React.useCallback((args: ICompanyDetailsFormFields) => {
        onSaveCompanyDetails(args)
            .then(() => showNotifications({ message: translate('modules.defaultAppSettings.companyDetails.successfulCompanyDetails'), type: 'success' }))
            .catch(() => showNotifications({ message: translate('modules.defaultAppSettings.companyDetails.failureinSavingCompanyDetails'), type: 'error' }));
    }, [onSaveCompanyDetails, showNotifications, translate]);

    return (
        <StyledFlexBox $margin='30px 0px 0px 0px' flexDirection='column' height='calc(100% - 46px)' justifyContent="space-between">
            <StackPanel orientation='vertical' gap="30" width="100%" height="calc(100% - 81px)" justifyContent="center" alignment="center">
                <Typography.H2 title={translate('modules.defaultAppSettings.companyDetails.title')}><Localize translate="modules.defaultAppSettings.companyDetails.title" /></Typography.H2>
                <CompanyDetailsForm />
            </StackPanel>
            <TabFooterButtons onSubmitClickHandler={handleSubmit(onSaveClick)} mutationLoading={mutationLoading} />
        </StyledFlexBox>
    );
};

export const CompanyDetails = React.memo((props: ICompanyDetailsProps) => {
    const { onSaveCompanyDetails, mutationLoading, ...rest } = props;
    const methods = useForm<ICompanyDetailsFormFields>({
        defaultValues: {
            eulaOptions: rest.eulaText ? 'customEULA' : 'standardEULA',
            ...rest
        },
        mode: 'onBlur'
    });

    return (
        <FormProvider {...methods}>
            <CompanyDetailsBase
                mutationLoading={mutationLoading}
                onSaveCompanyDetails={onSaveCompanyDetails} />
        </FormProvider>
    );
});
